import { useQuery } from '@tanstack/react-query';

import config from '@utils/config';
import {
    getAccessTokenOptions,
    getFrontTokenOptions,
    refreshTokenOptions,
} from '@utils/cookieConfig';

import { useCookies } from 'react-cookie';
import { doRefreshToken } from '@apis/utils';

enum SessionAPIEndpoints {
    'sessionInfo' = 'session/info',
    'refreshToken' = 'session/refresh',
}

const API_ENDPOINT = config.naraBackendURI;

export const getSessionInfo = async ({
    accessToken,
    setCookie,
}: {
    accessToken?: string;
    setCookie?: any;
}) => {
    if (!accessToken) {
        throw new Error('Access Token is Not Provided');
    }
    const response = await fetch(
        `${API_ENDPOINT}/${SessionAPIEndpoints.sessionInfo}`,
        {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
            },
        }
    );
    const data = await response.json();
    if (data.message === 'try refresh token') {
        // Use the shared refresh mutex to avoid concurrent refresh requests
        // that would trigger SuperTokens "token theft detected".
        const newTokens = await doRefreshToken();

        // Set the new tokens in the cookies
        setCookie(
            'accessToken',
            newTokens.accessToken,
            getAccessTokenOptions(newTokens.accessToken)
        );
        setCookie(
            'frontToken',
            newTokens.frontToken,
            getFrontTokenOptions(newTokens.accessToken)
        );
        if (newTokens.refreshToken) {
            setCookie(
                'refreshToken',
                newTokens.refreshToken,
                refreshTokenOptions
            );
        }
        // Retry the request with the new token
        const retryResponse = await fetch(
            `${API_ENDPOINT}/${SessionAPIEndpoints.sessionInfo}`,
            {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${newTokens.accessToken}`,
                },
            }
        );

        return retryResponse.json();
    }

    if (!response.ok) {
        throw new Error(`Session info failed: ${response.status}`);
    }

    return data;
};

export function useGetSessionInfo({ accessToken }: { accessToken?: string }) {
    const [cookies, setCookie] = useCookies([
        'accessToken',
        'frontToken',
        'refreshToken',
    ]);

    return useQuery({
        queryKey: ['getSessionInfo'],
        enabled: !!accessToken,
        retry: false,
        staleTime: 60_000,
        refetchOnWindowFocus: false,
        queryFn: () =>
            getSessionInfo({
                accessToken,
                setCookie,
            }),
    });
}

export const logoutSession = async (
    accessToken: string,
    email: string
): Promise<void> => {
    await fetch(`${API_ENDPOINT}/sessions/all`, {
        method: 'DELETE',
        headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
    });
};
